import {Avatar, cn} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {Icon} from "@iconify/react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, SearchSchemaInput, useNavigate, useSearch} from '@tanstack/react-router'
import {useWindowSize} from "@uidotdev/usehooks";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {apiAction, updateQueryData, updateQueryDataItem} from "../../../api.ts";
import img_male from "../../../assets/img_male.png";
import spMark from "../../../assets/favicon_scorepal.png";
import Content from "../../../components/Content.tsx";
import CreateConversationModal from "../../../components/modals/CreateConversationModal.tsx";
import PButton from "../../../components/PButton.tsx";
import {ApplicationStatus, MessageRelatedType, MessageType} from "../../../constants.ts";
import {useGlobalModal} from "../../../core/GlobalModal.tsx";
import {usePusher} from "../../../core/PusherContext.tsx";
import Header from "../../../Header.tsx";
import {useJwtAuth, useJwtUser} from "../../../JWTAuthProvider.tsx";
import services from "../../../services";
import {Conversation} from "../../../types";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/messages/')({
    component:      RouteComponent,
    validateSearch: (s:{to?:string, uid?:string} & SearchSchemaInput) => {
        return {
            to: s.to as string,
            uid: s.uid as string,
        }
    }
})

/*
 * Messages — a conversation list on the left, the open thread on the right. The same data flow
 * as before (conversations index/show, messages store, Pusher for live arrivals); only the
 * presentation changed: readable rows with previews and unread marks, day-grouped bubbles with
 * timestamps, a sticky thread header, and a composer that sends on Enter.
 */

const SYSTEM: any = {uid: 'system'};

const friendName = (c: Conversation | any) => c?.uid === 'system'
    ? 'ScorePal'
    : (c?._friend?.club?.name || c?._friend?.community?.name || c?._friend?.name || 'Conversation');
const friendSubtitle = (c: Conversation | any) => c?.uid === 'system'
    ? 'Notifications from ScorePal'
    : ((c?._friend?.club?.name || c?._friend?.community?.name) ? c?._friend?.name : (c?._friend?.email || ''));
const friendAvatar = (c: Conversation | any) => c?.uid === 'system' ? spMark : (c?._friend?.profile_thumb_url || img_male);
// The SP mark is wider than tall: fit it inside the circle on white rather than cropping it.
const markAvatar = (src: string) => src === spMark ? {base: 'bg-white', img: 'object-contain p-1'} : undefined;

const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const dayLabel = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (sameDay(d, now)) return 'Today';
    if (sameDay(d, yesterday)) return 'Yesterday';
    return d.toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short', ...(d.getFullYear() !== now.getFullYear() ? {year: 'numeric'} : {})});
};
const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'});
/** Row timestamp: time today, weekday this week, else a short date. */
const rowTime = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    if (sameDay(d, now)) return timeLabel(iso);
    const diff = (now.getTime() - d.getTime()) / 86400000;
    if (diff < 6) return d.toLocaleDateString('en-GB', {weekday: 'short'});
    return d.toLocaleDateString('en-GB', {day: 'numeric', month: 'short'});
};

function ConversationRow({conversation, isSelected, meUid, onOpen}: {
    conversation: Conversation | any; isSelected: boolean; meUid?: string; onOpen: (uid: string) => void;
}) {
    const isSystem = conversation.uid === 'system';
    const last = conversation.last_message;
    const unread = !isSystem && !!last && last.sender?.uid !== meUid && !last.read_at;
    return (
        <button type="button" onClick={() => onOpen(conversation.uid)}
                className={cn(
                    "flex w-full items-center gap-3 px-3 py-2.5 text-left transition border-l-[3px]",
                    isSelected ? "border-l-primary bg-primary/5" : "border-l-transparent hover:bg-default-100 dark:hover:bg-neutral-800",
                )}>
            <Avatar size="md" src={friendAvatar(conversation)} classNames={markAvatar(friendAvatar(conversation))} className="shrink-0" isBordered={isSystem} color={isSystem ? 'primary' : 'default'} />
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                    <span className={cn("truncate text-sm", unread ? "font-semibold text-foreground" : "font-medium text-foreground/90")}>
                        {friendName(conversation)}
                    </span>
                    {!isSystem && <span className="shrink-0 text-[11px] text-default-400">{rowTime(last?.created_at)}</span>}
                </div>
                <div className="flex items-center gap-2">
                    <span className={cn("truncate text-xs", unread ? "text-foreground" : "text-default-500")}>
                        {isSystem ? 'Invitations, bookings and updates' : (last?.content || 'No messages yet')}
                    </span>
                    {unread && <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
                </div>
            </div>
        </button>
    );
}

/** The action row a system message may carry: invitation accept/reject, or a link to the thing it is about. */
function MessageActions({message, onUpdateApplication}: {message: any; onUpdateApplication: (appl: any, status: any) => void}) {
    const navigate = useNavigate();
    const user = useJwtUser();
    const links: React.ReactNode[] = [];
    const go = (label: string, to: string, key: string) => links.push(
        <PButton key={key} size="sm" variant="flat" color="primary" onPress={() => navigate({to: to as any})}>{label}</PButton>
    );

    if (message.related_uid) {
        const inviteish = [MessageType.Invitation, MessageType.InvitationRejected, MessageType.InvitationApproved].includes(message.message_type);
        if (inviteish) {
            if (message.related_type === MessageRelatedType.Game) go('View game', `/profile/games/${message.related_uid}`, 'game');
            if (message.related_type === MessageRelatedType.League) go('View league', `/leagues/${message.related_uid}/home`, 'league');
            if (message.related_type === MessageRelatedType.Tournament) go('View tournament', `/tournaments/${message.related_uid}/home`, 'tournament');
            if (message.related_type === MessageRelatedType.Booking) go('View bookings', '/courts-booking/', 'booking');
        }
        if (message.related_type === MessageRelatedType.Booking) {
            if (message.message_type === MessageType.BookingCreated || message.message_type === MessageType.BookingPlayerCancelled) go('Manage bookings', `/clubs/${user?.club?.uid}/bookings/`, 'manage');
            if (message.message_type === MessageType.BookingApproved || message.message_type === MessageType.BookingOrganizerCancelled) go('View bookings', '/courts-booking/', 'view');
        }
        if (message.message_type === MessageType.AssessmentCompleted && message.related_type === MessageRelatedType.Assessment) go('View assessment', `/abilities/${message.related_uid}/`, 'assessment');
    }

    let invitation: React.ReactNode = null;
    if (message.message_type === MessageType.Invitation) {
        switch (message.application?.status) {
            case ApplicationStatus.Invited:
                invitation = <>
                    <PButton size="sm" color="primary" onPress={() => onUpdateApplication(message.application, ApplicationStatus.Approved)}>Accept invitation</PButton>
                    <PButton size="sm" variant="light" onPress={() => onUpdateApplication(message.application, ApplicationStatus.Rejected)}>Decline</PButton>
                </>;
                break;
            case ApplicationStatus.Approved: invitation = <span className="text-xs text-success">Invitation accepted</span>; break;
            case ApplicationStatus.Rejected: invitation = <span className="text-xs text-default-500">Invitation declined</span>; break;
            default: invitation = <span className="text-xs text-default-500">Invitation has expired</span>;
        }
    }
    if (!links.length && !invitation) return null;
    return <div className="mt-2 flex flex-wrap items-center gap-2">{invitation}{links}</div>;
}

function Bubble({message, mine, showAvatar, avatar, onUpdateApplication}: {
    message: any; mine: boolean; showAvatar: boolean; avatar: string; onUpdateApplication: (appl: any, status: any) => void;
}) {
    return (
        <div className={cn("flex items-end gap-2", mine ? "justify-end" : "justify-start")}>
            {!mine && <div className="w-7 shrink-0">{showAvatar && <Avatar size="sm" src={avatar} classNames={markAvatar(avatar)} className="h-7 w-7" />}</div>}
            <div className={cn(
                "max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm",
                mine ? "rounded-br-md bg-primary text-white" : "rounded-bl-md bg-default-100 text-foreground dark:bg-neutral-800",
            )}>
                <div className="whitespace-pre-wrap break-words">{message.content}</div>
                <MessageActions message={message} onUpdateApplication={onUpdateApplication} />
                <div className={cn("mt-1 text-[10px]", mine ? "text-white/70 text-right" : "text-default-400")}>{timeLabel(message.created_at)}</div>
            </div>
        </div>
    );
}

function RouteComponent() {
    const navigate = useNavigate();
    const jwt = useJwtAuth();
    const search = useSearch<any>({} as any);
    const user = useJwtUser();
    const globalModal = useGlobalModal();
    const queryClient = useQueryClient();
    const pusher = usePusher();
    const [message, setMessage] = useState("");
    const [filter, setFilter] = useState("");

    const messagesEndRef = useRef<HTMLDivElement>(null);

    function openChat(uid: string) {
        navigate({to: '/messages/', search: {uid: uid}});
    }

    const pusherHandle = (event: any) => {
        if (event.event === "ModelCreated" && event.table === 'messages') {
            queryClient.setQueryData(queryKeys.conversations(event.data.conversation_uid), (data: any) => {
                return {...data, messages: [...(data?.messages || []), event.data]};
            });
            refreshConversation(event.data);
        }
    };

    useEffect(() => {
        pusher.on(pusherHandle);
        return () => {
            pusher.off(pusherHandle);
        }
    }, []);

    const conversationsQuery = useQuery({
        queryKey: queryKeys.conversations(),
        queryFn:  async () => services.conversations.index(),
        enabled:  user !== undefined,
    })
    const conversationQuery = useQuery({
        queryKey: queryKeys.conversations(search.uid),
        queryFn:  async () => services.conversations.show(search.uid),
        enabled:  user !== undefined && !!search.uid,
    })

    const sendMessage = useMutation({
        mutationFn: async (message: string) => services.messages.store({conversation_uid: search.uid, content: message}),
        onMutate:   () => {
            setMessage(""); // clears the textarea
            updateQueryDataItem(queryClient, queryKeys.conversations(search.uid), {
                messages: [...(conversationQuery.data?.messages || []), {
                    uid:              +new Date(),
                    sender:           user,
                    content:          message,
                    created_at:       new Date().toISOString(),
                    conversation_uid: search.uid
                }],
            })
        },
        onSuccess:  (data) => {
            refreshConversation(data);
        },
    })

    function refreshConversation(data) {
        queryClient.invalidateQueries({queryKey: queryKeys.conversations(search.uid)})
        updateQueryData(queryClient, queryKeys.conversations(), search.uid, {last_message_uid: data.uid, last_message: data})
    }

    const updateApplication = (appl, status) => {
        apiAction('applications-update', {
            uid:    appl.uid,
            values: {version: (appl.version || 0) + 1, status},
        }).then(() => {
            queryClient.invalidateQueries({queryKey: queryKeys.messages()});
            queryClient.invalidateQueries({queryKey: queryKeys.conversations(search.uid)});
        });
    };

    const conversationUid = search.uid;
    const isSystem = conversationUid === 'system';

    const size = useWindowSize();
    const isSm = (!!size.width && size.width < 768);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({behavior: "instant"});
    }, [conversationQuery.data]);

    useEffect(() => {
        if (user?.unread_messages_count) {
            jwt.reloadUser();
        }
    }, [conversationQuery.data]);

    // Conversations newest first, filtered by the search box.
    const conversations = useMemo(() => {
        const list = [...(conversationsQuery.data || [])].sort((a: any, b: any) =>
            (b.last_message?.created_at || b.created_at || '').localeCompare(a.last_message?.created_at || a.created_at || ''));
        const q = filter.trim().toLowerCase();
        return q ? list.filter((c: any) => friendName(c).toLowerCase().includes(q)) : list;
    }, [conversationsQuery.data, filter]);

    const current: any = isSystem ? SYSTEM : conversationQuery.data?.conversation;
    const messages: any[] = conversationQuery.isEnabled ? (conversationQuery.data?.messages || []) : [];

    const send = () => {
        const text = message.trim();
        if (!text || isSystem) return;
        sendMessage.mutate(text);
    };

    return <>
        <Header showBackUrl={false} />

        <Content fullHeight={true}>
            {/* A fixed-height canvas so the list and thread scroll inside it rather than the page growing with the chat. */}
            <div className="grid overflow-hidden rounded-2xl border border-default-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-[#252525] md:grid-cols-[300px_1fr]"
                 style={{height: 'calc(100dvh - 150px)', minHeight: 480}}>

                {/* Conversation list */}
                {(!isSm || !search.uid) &&
                    <div className="flex h-full min-h-0 flex-col border-r border-default-200 dark:border-neutral-800">
                        <div className="flex items-center justify-between gap-2 px-4 py-3">
                            <div>
                                <h1 className="text-lg font-semibold leading-tight">Messages</h1>
                                {conversationsQuery.data && <div className="text-xs text-default-400">{conversationsQuery.data.length} conversation{conversationsQuery.data.length === 1 ? '' : 's'}</div>}
                            </div>
                            <PButton color="primary" size="sm" isIconOnly aria-label="New message" title="New message"
                                     onPress={() => globalModal.open(<CreateConversationModal />)}>
                                <Icon icon="material-symbols-light:chat-add-on-outline" className="text-2xl" />
                            </PButton>
                        </div>
                        <div className="px-3 pb-2">
                            <div className="flex items-center gap-2 rounded-lg bg-default-100 px-2.5 py-1.5 dark:bg-neutral-800">
                                <Icon icon="material-symbols-light:search" className="text-xl text-default-400" />
                                <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search"
                                       className="w-full bg-transparent text-sm outline-none placeholder:text-default-400" />
                            </div>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto">
                            <ConversationRow conversation={SYSTEM} isSelected={isSystem} meUid={user?.uid} onOpen={openChat} />
                            {conversationsQuery.isLoading && <div className="flex justify-center py-6"><Spinner size="sm" /></div>}
                            {conversations.map((conversation: any) =>
                                <ConversationRow key={conversation.uid} conversation={conversation} isSelected={search.uid === conversation.uid} meUid={user?.uid} onOpen={openChat} />
                            )}
                            {conversationsQuery.data && conversations.length === 0 && (
                                <div className="px-4 py-8 text-center text-sm text-default-400">
                                    {filter ? 'No conversations match.' : 'No conversations yet. Start one with the button above.'}
                                </div>
                            )}
                        </div>
                    </div>
                }

                {/* Thread */}
                {(!isSm || search.uid) &&
                    <div className="flex h-full min-h-0 flex-col">
                        {!search.uid ? (
                            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-default-400">
                                <Icon icon="material-symbols-light:forum-outline" className="text-5xl" />
                                <div className="text-sm">Pick a conversation, or start a new one.</div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-3 border-b border-default-200 px-4 py-2.5 dark:border-neutral-800">
                                    {isSm && (
                                        <button type="button" onClick={() => navigate({to: "/messages/"})} aria-label="Back to conversations"
                                                className="-ml-1 rounded-full p-1 text-default-500 hover:bg-default-100">
                                            <Icon icon="material-symbols-light:arrow-back" className="text-2xl" />
                                        </button>
                                    )}
                                    {current
                                        ? <>
                                            <Avatar size="sm" src={friendAvatar(current)} classNames={markAvatar(friendAvatar(current))} />
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-semibold">{friendName(current)}</div>
                                                {friendSubtitle(current) && <div className="truncate text-xs text-default-400">{friendSubtitle(current)}</div>}
                                            </div>
                                        </>
                                        : <Spinner size="sm" />}
                                    {conversationQuery.isFetching && !conversationQuery.isLoading && <Spinner size="sm" className="ml-auto" />}
                                </div>

                                <div className="min-h-0 flex-1 overflow-y-auto bg-default-50/60 px-4 py-3 dark:bg-neutral-900/40">
                                    {conversationQuery.isLoading ? (
                                        <div className="flex h-full items-center justify-center"><Spinner size="sm" /></div>
                                    ) : messages.length === 0 ? (
                                        <div className="flex h-full flex-col items-center justify-center gap-1 text-default-400">
                                            <Icon icon="material-symbols-light:waving-hand-outline" className="text-4xl" />
                                            <div className="text-sm">{isSystem ? 'No notifications yet.' : 'No messages yet. Say hello.'}</div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-1.5">
                                            {messages.map((m, i) => {
                                                const prev = messages[i - 1];
                                                const newDay = !prev || !sameDay(new Date(prev.created_at), new Date(m.created_at));
                                                const mine = !isSystem && m.sender?.uid === user?.uid;
                                                const sameSenderAsPrev = !!prev && !newDay && (prev.sender?.uid ?? 'system') === (m.sender?.uid ?? 'system');
                                                const avatar = isSystem ? spMark : (m.sender?.profile_thumb_url || friendAvatar(current));
                                                return (
                                                    <React.Fragment key={m.uid}>
                                                        {newDay && (
                                                            <div className="my-2 flex items-center gap-3 text-[11px] uppercase tracking-wide text-default-400">
                                                                <span className="h-px flex-1 bg-default-200 dark:bg-neutral-700" />
                                                                {dayLabel(m.created_at)}
                                                                <span className="h-px flex-1 bg-default-200 dark:bg-neutral-700" />
                                                            </div>
                                                        )}
                                                        <Bubble message={m} mine={mine} showAvatar={!sameSenderAsPrev} avatar={avatar} onUpdateApplication={updateApplication} />
                                                    </React.Fragment>
                                                );
                                            })}
                                            <div ref={messagesEndRef} />
                                        </div>
                                    )}
                                </div>

                                {isSystem ? (
                                    <div className="border-t border-default-200 px-4 py-2.5 text-center text-xs text-default-400 dark:border-neutral-800">
                                        These are notifications from ScorePal. Use the chat button bottom-right to reach support.
                                    </div>
                                ) : (
                                    <div className="flex items-end gap-2 border-t border-default-200 p-3 dark:border-neutral-800">
                                        <textarea
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                                            placeholder="Write a message… (Enter to send, Shift+Enter for a new line)"
                                            rows={2}
                                            className="w-full resize-none rounded-xl border border-default-200 bg-default-50 px-3.5 py-2.5 text-sm outline-none focus:border-primary dark:border-neutral-700 dark:bg-neutral-800"
                                        />
                                        <button type="button" onClick={send} disabled={!message.trim() || sendMessage.isPending} aria-label="Send"
                                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white transition hover:opacity-90 disabled:opacity-40">
                                            <Icon icon="mdi:send" className="text-xl" />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                }
            </div>
        </Content>
    </>
}
